import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError, cachedGet } from "@/lib/api";
import { BookOpenCheck, Receipt, Heart, Car, AlertTriangle, Calendar, ArrowRight, UserCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import EmergencyContactCard from "@/components/EmergencyContactCard";

export default function WorkerDashboard() {
  const { user } = useAuth();
  const [tss, setTss] = useState([]);
  const [invs, setInvs] = useState([]);
  const [roster, setRoster] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const today = new Date(); const start = new Date(today); start.setDate(start.getDate() - 7);
        const fmt = (d) => d.toISOString().slice(0, 10);
        const rosterParams = { start: fmt(today), end: fmt(new Date(today.getTime() + 14 * 86400000)) };
        await Promise.all([
          cachedGet("/timesheets", null, (data) => setTss(data)),
          cachedGet("/invoices", null, (data) => setInvs(data)),
          cachedGet("/roster", rosterParams, (data) => setRoster(data)),
        ]);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const weekTotal = tss.filter((t) => {
    const d = new Date(t.date); const now = new Date();
    return (now - d) / 86400000 <= 7;
  }).reduce((s, t) => s + (t.total_pay || 0), 0);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="worker-dashboard">
      <header>
        <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground font-bold">Hello support worker</p>
        <h1 className="text-4xl sm:text-5xl font-display font-black tracking-tight">
          {user?.full_name?.split(" ")[0]} <span className="inline-block animate-float">✨</span>
        </h1>
        <p className="text-muted-foreground mt-1">Log your shift, view your roster, and submit invoices.</p>
      </header>

      <Card className="rounded-3xl p-6 sm:p-8 border-2 relative overflow-hidden text-foreground"
            style={{ background: "linear-gradient(135deg, hsl(var(--worker) / 0.25), hsl(var(--sun) / 0.2))" }}
            data-testid="worker-week-card">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-foreground/70">This week's earnings</p>
        <div className="font-display font-black text-5xl sm:text-6xl mt-2" data-testid="worker-week-amount">${weekTotal.toFixed(2)}</div>
        <Link to="/timesheets/new"><Button className="rounded-full mt-5 min-h-[52px] font-bold text-base" data-testid="log-shift-btn"
          style={{ background: "hsl(var(--worker))", color: "hsl(var(--worker-fg))" }}>
          <BookOpenCheck className="w-4 h-4 mr-2" /> Log a shift
        </Button></Link>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile to="/timesheets" label="Timesheets" value={tss.length} icon={BookOpenCheck} />
        <Tile to="/invoices" label="Invoices" value={invs.length} icon={Receipt} />
        <Tile to="/roster" label="Upcoming shifts" value={roster.length} icon={Calendar} />
        <Tile to="/profile" label="My profile" value={"Edit"} icon={UserCircle} />
      </div>

      <EmergencyContactCard />

      <Card className="rounded-3xl p-6 border-2" data-testid="recent-timesheets-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-extrabold text-2xl">Recent timesheets</h3>
          <Link to="/timesheets"><Button variant="ghost" size="sm" className="rounded-full font-bold">All <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
        </div>
        {tss.length === 0 ? (
          <p className="text-sm text-muted-foreground">No shifts yet.</p>
        ) : (
          <div className="divide-y">
            {tss.slice(0, 5).map((t) => (
              <div key={t.id} className="py-3 flex items-center justify-between gap-2" data-testid={`worker-ts-row-${t.id}`}>
                <div>
                  <div className="font-bold">{t.date} <span className="text-muted-foreground font-normal">· {t.start_time}–{t.end_time}</span></div>
                  <div className="text-xs text-muted-foreground">{t.shift_duration_hours}h · {t.pay_rate_type.replace(/_/g, " ")}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold">${t.total_pay.toFixed(2)}</div>
                  <span className={`text-[10px] uppercase font-bold tracking-wider ${t.status === "approved" ? "text-emerald-700" : t.status === "rejected" ? "text-destructive" : "text-amber-600"}`}>{t.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Tile({ to, label, value, icon: Icon }) {
  return (
    <Link to={to} className="rounded-2xl p-4 bg-card border-2 card-lift" data-testid={`wk-tile-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <Icon className="w-5 h-5 text-worker" strokeWidth={2.6} />
      <div className="font-display font-black text-2xl mt-2">{value}</div>
      <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
    </Link>
  );
}
